import React, { memo } from "react";
import { Customer } from "@/types/customer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  User,
  Phone,
  Briefcase,
  MapPin,
  Hash,
  ArrowLeft,
  Shield,
  FileText,
  Info,
  Menu,
  X,
} from "lucide-react";

interface CustomerPaymentHeaderProps {
  customer: Customer;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  onClose: () => void;
}

export const CustomerPaymentHeader = memo(function CustomerPaymentHeader({
  customer,
  mobileMenuOpen,
  setMobileMenuOpen,
  onClose,
}: CustomerPaymentHeaderProps) {
  return (
    <>
      {/* Mobile Header */}
      <header className="md:hidden border-b bg-background z-10">
        <div className="flex items-center h-14 px-4 w-full">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <span className="font-bold text-base">{customer.fullName}</span>
            <Popover>
              <PopoverTrigger asChild>
                <button className="h-7 w-7 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 cursor-pointer">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="start" dir="rtl">
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">کۆد:</span>
                    <span className="font-medium">{customer.customerCode}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">ناو:</span>
                    <span className="font-medium">{customer.fullName}</span>
                  </div>
                  {customer.job && (
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">پیشە:</span>
                      <span className="font-medium">{customer.job}</span>
                    </div>
                  )}
                  <a 
                    href={`tel:${customer.phone1}`}
                    className="flex items-center gap-2 p-2 rounded-md bg-green-50 hover:bg-green-100 transition-colors cursor-pointer border border-green-200"
                  >
                    <Phone className="h-4 w-4 text-green-600" />
                    <span className="text-green-700">مۆبایل ١:</span>
                    <span className="font-medium text-green-700" dir="ltr">{customer.phone1}</span>
                  </a>
                  {customer.phone2 && (
                    <a 
                      href={`tel:${customer.phone2}`}
                      className="flex items-center gap-2 p-2 rounded-md bg-green-50 hover:bg-green-100 transition-colors cursor-pointer border border-green-200"
                    >
                      <Phone className="h-4 w-4 text-green-600" />
                      <span className="text-green-700">مۆبایل ٢:</span>
                      <span className="font-medium text-green-700" dir="ltr">{customer.phone2}</span>
                    </a>
                  )}
                  {customer.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">ناونیشان:</span>
                      <span className="font-medium">{customer.address}</span>
                    </div>
                  )}
                  {customer.guarantorName && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">کەفیل:</span>
                      <span className="font-medium">{customer.guarantorName}</span>
                    </div>
                  )}
                  {customer.guarantorPhone && (
                    <a 
                      href={`tel:${customer.guarantorPhone}`}
                      className="flex items-center gap-2 p-2 rounded-md bg-green-50 hover:bg-green-100 transition-colors cursor-pointer border border-green-200"
                    >
                      <Phone className="h-4 w-4 text-green-600" />
                      <span className="text-green-700">ژمارەی کەفیل:</span>
                      <span className="font-medium text-green-700" dir="ltr">{customer.guarantorPhone}</span>
                    </a>
                  )}
                  {customer.notes && (
                    <div className="flex items-start gap-2 pt-2 border-t">
                      <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <span className="text-muted-foreground">تێبینی:</span>
                      <span className="font-medium flex-1">{customer.notes}</span>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex-1"></div>
          <Button variant="outline" size="sm" onClick={onClose} className="flex items-center gap-2">
            گەڕانەوە
            <ArrowLeft className="h-4 w-4 mr-2" />
          </Button>
        </div>
      </header>

      {/* Desktop Top Bar */}
      <div className="hidden md:block border-b bg-background z-10">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-xs h-5">
                <Hash className="h-3 w-3 ml-1" />
                {customer.customerCode}
              </Badge>
              <h1 className="text-base font-semibold">{customer.fullName}</h1>
              {customer.job && (
                <Badge variant="secondary" className="text-xs h-5">
                  <Briefcase className="h-3 w-3 ml-1" />
                  {customer.job}
                </Badge>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="h-6 w-6 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 cursor-pointer">
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" align="start" dir="rtl">
                  <div className="space-y-3 text-sm">
                    <a 
                      href={`tel:${customer.phone1}`}
                      className="flex items-center gap-2 p-2 rounded-md bg-green-50 hover:bg-green-100 transition-colors cursor-pointer border border-green-200"
                    >
                      <Phone className="h-4 w-4 text-green-600" />
                      <span className="text-green-700">مۆبایل ١:</span>
                      <span className="font-medium text-green-700" dir="ltr">{customer.phone1}</span>
                    </a>
                    {customer.phone2 && (
                      <a 
                        href={`tel:${customer.phone2}`}
                        className="flex items-center gap-2 p-2 rounded-md bg-green-50 hover:bg-green-100 transition-colors cursor-pointer border border-green-200"
                      >
                        <Phone className="h-4 w-4 text-green-600" />
                        <span className="text-green-700">مۆبایل ٢:</span>
                        <span className="font-medium text-green-700" dir="ltr">{customer.phone2}</span>
                      </a>
                    )}
                    {customer.address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">ناونیشان:</span>
                        <span className="font-medium">{customer.address}</span>
                      </div>
                    )}
                    {customer.guarantorName && (
                      <div className="flex items-center gap-2 pt-2 border-t">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">کەفیل:</span>
                        <span className="font-medium">{customer.guarantorName}</span>
                      </div>
                    )}
                    {customer.guarantorPhone && (
                      <a 
                        href={`tel:${customer.guarantorPhone}`}
                        className="flex items-center gap-2 p-2 rounded-md bg-green-50 hover:bg-green-100 transition-colors cursor-pointer border border-green-200"
                      >
                        <Phone className="h-4 w-4 text-green-600" />
                        <span className="text-green-700">ژمارەی کەفیل:</span>
                        <span className="font-medium text-green-700" dir="ltr">{customer.guarantorPhone}</span>
                      </a>
                    )}
                    {customer.notes && (
                      <div className="flex items-start gap-2 pt-2 border-t">
                        <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <span className="text-muted-foreground">تێبینی:</span>
                        <span className="font-medium flex-1">{customer.notes}</span>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} className="flex items-center gap-2">
            گەڕانەوە
            <ArrowLeft className="h-4 w-4 mr-2" />
          </Button>
        </div>
      </div>
    </>
  );
});
